import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { Field } from '@app/api/types'

import { BookingState, BookingSteps, SetTimeSlotsAction } from './types'
import { AbTestVariants } from '../ui'
import { getBookingFlow } from '../../ui/pages/Booking/UnifiedWorkflow/AbTestFlows'

export const INITIAL_STATE: BookingState = {
  status: 'initial',
  createLeadStatus: 'initial',
  initBookingFlowStatus: 'failure',
  initBookingFlowError: {
    message: 'Something went wrong. Please reload app and try again'
  },
  createLeadError: '',
  error: '',
  flowData: {
    stepsNumber: getBookingFlow(AbTestVariants.Control).stepsNumber,
    backStep: false
  },
  inputData: {}
}

export const slice = createSlice({
  name: 'booking',
  initialState: INITIAL_STATE,

  reducers: {
    setBookingStep: (
      state,
      action: PayloadAction<{
        readonly step: BookingSteps
        readonly backStep?: boolean
      }>
    ) => {
      state.flowData.currentStep = action.payload.step
      state.flowData.backStep = action.payload.backStep
    },
    errorBackgroundRequest: (state, action: PayloadAction<{ message: string }>) => {
      state.status = 'failure'
      state.error = action.payload.message
    },
    setTimeSlots: (state, action: PayloadAction<SetTimeSlotsAction>) => {
      state.flowData.dateFrom = action.payload.dateFrom
      state.flowData.dateTo = action.payload.dateTo
      state.flowData.timeSlots = action.payload.timeSlots
    },
    setStepsNumber: (state, action: PayloadAction<number>) => {
      state.flowData.stepsNumber = action.payload
    },
    requestUpdateBookingInputData: state => {
      state.status = 'pending'
    },
    successUpdateBookingInputData: (state, { payload }: PayloadAction<Field | Field[]>) => {
      state.status = 'success'

      if (Array.isArray(payload)) {
        for (const field of payload) {
          state.inputData[field.fieldName] = field.fieldValue
        }
      } else {
        state.inputData[payload.fieldName] = payload.fieldValue
      }
    },
    errorUpdateBookingInputData: (state, action: PayloadAction<string>) => {
      state.status = 'failure'
      state.error = action.payload
    },
    setDiagnosisUserAnswer: (state, action: PayloadAction<string>) => {
      state.inputData.diagnosisUserAnswer = action.payload
    },
    setAgeInYearsAnswer: (state, action: PayloadAction<number>) => {
      state.inputData.ageInYears = action.payload
    },
    setIsParentUserAnswer: (state, action: PayloadAction<string>) => {
      state.inputData.isParentUserAnswer = action.payload
    },
    setLegalGuardianUserAnswer: (state, action: PayloadAction<string>) => {
      state.inputData.legalGuardianUserAnswer = action.payload
    },
    setCurrentUtcTime: (state, action: PayloadAction<string>) => {
      state.status = 'success'
      state.flowData.currentUtcTime = action.payload
    },
    setIsTimeSlotChosen: (state, action: PayloadAction<boolean>) => {
      state.flowData.isTimeSlotChosen = action.payload
    },
    requestConfirmBooking: state => {
      state.status = 'pending'
    },
    successConfirmBooking: state => {
      state.status = 'success'
    },
    errorConfirmBooking: state => {
      state.status = 'failure'
    },
    requestCreateLead: state => {
      state.createLeadStatus = 'pending'
    },
    successCreateLead: state => {
      state.createLeadStatus = 'success'
    },
    errorCreateLead: (state, action: PayloadAction<string>) => {
      state.createLeadStatus = 'failure'
      state.createLeadError = action.payload
    },
    setInitialCreateLeadStatus: state => {
      state.createLeadStatus = 'initial'
    },
    requestInitBookingUserId: state => {
      state.initBookingFlowStatus = 'initial'
    },
    successInitBookingUserId: (state, { payload }: PayloadAction<string>) => {
      state.bookingUserId = payload
      state.initBookingFlowStatus = 'success'
    },
    errorInitBookingUserId: (state, action: PayloadAction<string>) => {
      state.initBookingFlowStatus = 'failure'
      state.initBookingFlowError = action.payload
    },
    resetBookingFlowInput: state => {
      state.inputData = {
        ...INITIAL_STATE.inputData
      }

      state.bookingUserId = INITIAL_STATE.bookingUserId
      state.initBookingFlowStatus = 'initial'
      state.initBookingFlowError = INITIAL_STATE.initBookingFlowError
    },
    setAuthFlowId(state, action: PayloadAction<string>) {
      state.authFlowId = action.payload
    }
  }
})

export const { actions } = slice
export const bookingReducer = slice.reducer
